import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import {
  DunaEventsFilterComponent,
  FilterSettings,
} from '../../components/duna/duna-events-filter/duna-events-filter.component';
import { environment } from '../../environment/environment';

@Injectable({ providedIn: 'root' })
export class DunaEventsFilterOverlayService {
  private overlayRef: OverlayRef | null = null;
  private backdropClickSub?: Subscription;
  private paneStopHandler?: (event: Event) => void;
  private overlayPane?: HTMLElement;
  private component?: HTMLElement;

  constructor(private overlay: Overlay, private injector: Injector) {}

  open(settings?: FilterSettings | null): void {
    if (this.overlayRef) {
      this.close();
    }

    this.overlayPane = undefined;
    this.component = undefined;
    this.paneStopHandler = undefined;

    const viewportWidth = Math.round(
      window.visualViewport?.width || window.innerWidth
    );
    const isMobile = viewportWidth <= environment.mobileWidth;
    const panelClass = isMobile
      ? ['duna-filter-overlay-panel', 'mobile-sheet']
      : 'duna-filter-overlay-panel';

    const positionStrategy = isMobile
      ? this.overlay.position().global().bottom('0').centerHorizontally()
      : this.overlay
          .position()
          .global()
          .centerHorizontally()
          .centerVertically();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'duna-filter-backdrop',
      panelClass,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      disposeOnNavigation: true,
    });

    const portal = new ComponentPortal(DunaEventsFilterComponent, null, this.injector);
    const componentRef = this.overlayRef.attach(portal);
    const instance = componentRef.instance;
    instance.isOverlayMode = true;
    instance.isMobileOverlay = isMobile;
    if (settings) {
      instance.settings = settings;
    }

    this.applyFilterOverlayStacking();
    this.backdropClickSub?.unsubscribe();
    this.backdropClickSub = this.overlayRef
      .backdropClick()
      .subscribe(() => this.close());
  }

  private applyFilterOverlayStacking(): void {
    if (!this.overlayRef) {
      return;
    }

    const overlayPane = this.overlayRef.overlayElement;
    const backdrop = this.overlayRef.backdropElement;
    const component = overlayPane.querySelector(
      'app-duna-events-filter'
    ) as HTMLElement | null;

    overlayPane.style.setProperty('z-index', '1002', 'important');
    overlayPane.style.setProperty('background', '#ffffff', 'important');
    overlayPane.style.setProperty('pointer-events', 'auto', 'important');

    if (backdrop) {
      backdrop.style.setProperty('z-index', '1001', 'important');
      backdrop.style.setProperty('pointer-events', 'auto', 'important');
    }

    this.overlayPane = overlayPane;
    this.component = component ?? undefined;
  }

  close(): void {
    this.backdropClickSub?.unsubscribe();
    this.backdropClickSub = undefined;

    // Remove pane and component event listeners
    if (this.paneStopHandler) {
      if (this.overlayPane) {
        this.overlayPane.removeEventListener('click', this.paneStopHandler, true);
        this.overlayPane.removeEventListener('mousedown', this.paneStopHandler, true);
        this.overlayPane.removeEventListener('touchstart', this.paneStopHandler, true);
      }
      if (this.component) {
        this.component.removeEventListener('click', this.paneStopHandler, true);
        this.component.removeEventListener('mousedown', this.paneStopHandler, true);
        this.component.removeEventListener('touchstart', this.paneStopHandler, true);
      }
      this.paneStopHandler = undefined;
    }
    
    // Clear element references
    this.overlayPane = undefined;
    this.component = undefined;
    
    // Dispose overlay (this should remove all DOM elements)
    if (this.overlayRef) {
      try {
      this.overlayRef.dispose();
      } catch (e) {
        console.warn('[DunaEventsFilterOverlay] Error disposing overlay:', e);
      }
    }
    
    // ALWAYS clear overlayRef, even if dispose failed
    this.overlayRef = null;
    
    // Double-check: Remove any leftover backdrop or overlay elements
    setTimeout(() => {
      const leftoverBackdrop = document.querySelector(
        '.cdk-overlay-backdrop.duna-filter-backdrop'
      ) as HTMLElement;
      const leftoverPane = document.querySelector(
        '.cdk-overlay-pane.duna-filter-overlay-panel'
      ) as HTMLElement;
      const leftoverContainer = document.querySelector('.cdk-overlay-container') as HTMLElement;
      
      if (leftoverBackdrop || leftoverPane) {
        console.warn('[DunaEventsFilterOverlay] Found leftover elements after close, removing...');
        if (leftoverBackdrop) {
          leftoverBackdrop.remove();
        }
        if (leftoverPane) {
          leftoverPane.remove();
        }
      }
      
      // DON'T remove the overlay container - Angular CDK reuses it
      // Removing it causes issues when reopening the overlay
      // if (leftoverContainer && leftoverContainer.children.length === 0) {
      //   leftoverContainer.remove();
      // }
      
    }, 100);
  }
} 